<template>
  <div class="StoryBackRank" :class="{ 'is-superposed': position == null, 'is-flow': flow }" aria-hidden="true">
    <span v-if="label" class="rank-label">{{ label }}</span>
    <div v-for="(square, file) in squares" :key="file" class="rank-square">
      <img
        v-for="piece in square"
        :key="piece.letter"
        class="rank-piece"
        :src="CHESS_PIECE_SVGS[piece.letter]"
        alt=""
        :style="piece.delay == null ? undefined : { animationDelay: piece.delay, animationDuration: piece.duration }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { CHESS_PIECE_SVGS } from '@/views/composables/constants/components/gamChessboard.constants';
import { backRankFromNumber } from '@/views/main/handshakes/utils/handshake-game';

const props = defineProps<{
  /** A Chess960 number settles the rank. Without one, every piece sits on every square at once. */
  position?: number;
  /** Shown in the board row under the rank. */
  label?: string;
  /** In the lyrics scroll, on its own row, instead of pinned to the bottom of a page. */
  flow?: boolean;
}>();

const BACK_RANK_PIECES = ['K', 'Q', 'R', 'B', 'N'];

type RankPiece = { letter: string; delay?: string; duration?: string };

const squares: RankPiece[][] =
  props.position == null
    ? Array.from({ length: 8 }, () =>
        BACK_RANK_PIECES.map((letter) => {
          const duration = 2 + Math.random() * 2;
          return {
            letter,
            duration: `${duration.toFixed(2)}s`,
            delay: `${(-Math.random() * duration).toFixed(2)}s`,
          };
        }),
      )
    : backRankFromNumber(props.position).map((letter) => [{ letter }]);
</script>

<style lang="scss" scoped>
/* Sits on the bottom rank of the sheet's own eight-square checkerboard. */
.StoryBackRank {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  pointer-events: none;
}

.rank-label {
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12.5cqw;
  color: var(--color-white-50);
  font-size: 3.2cqw;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  line-height: 1;
  text-align: center;
}

.rank-square {
  position: relative;
  aspect-ratio: 1;
}

.rank-piece {
  position: absolute;
  inset: 8%;
  width: 84%;
  height: 84%;
  pointer-events: none;
}

/* One rank in the scroll: the pieces sit on their own squares, the number in the row below. */
.is-flow {
  position: relative;
  right: auto;
  bottom: auto;
  left: auto;
  width: 100%;
  background-image: repeating-conic-gradient(var(--color-dark-700) 0% 25%, var(--color-dark-600) 0% 50%);
  background-size: 25% 200%;

  &:has(.rank-label) {
    margin-bottom: 12.5cqw;
  }
}

.is-superposed .rank-piece {
  opacity: 0;
  animation: superposed ease-in-out infinite;
}

@keyframes superposed {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 0.85;
  }
}

@media (prefers-reduced-motion: reduce) {
  .is-superposed .rank-piece {
    opacity: 0.25;
    animation: none;
  }
}
</style>
